import { type Draft, produce } from 'immer';
import type { TreePickerItemWithValue } from './TreePickerItem';

const makeFilterTreePickerItemsByQuery =
  <TKey extends string>(query: string, expandedNodes: Draft<Set<TKey>>) =>
  (item: Draft<TreePickerItemWithValue<TKey>>): boolean => {
    const searchableString =
      `${item.label} ${item.description} ${item.additionalSearchableString ?? ''}`.toLowerCase();
    if (item.children.length === 0) {
      return searchableString.includes(query);
    }

    // Remember that the filter also picks expandedNodes from the subtree which is desired,
    // regardless if we replace item.children with newChildren or keep unfiltered ones.
    // Otherwise, expandedNodes won't be collected from descendants of matching inner nodes
    // and these won't be properly expanded.
    const newChildren = item.children.filter(
      makeFilterTreePickerItemsByQuery(query, expandedNodes),
    );

    const matchedDescendant = newChildren.length > 0;
    const matchedCurrent = searchableString.includes(query);
    if (matchedDescendant) {
      expandedNodes.add(item.key);
      if (!matchedCurrent) {
        // When an inner node matches, we show all its children unfiltered
        // (only matched descendants will be expanded up to the match).
        // We filter children only for nodes that do not match the query
        // but are shown because some of their descendant matched.
        item.children = newChildren;
      }
    }

    return matchedCurrent || matchedDescendant;
  };

export const searchTreePickerItems = <TKey extends string>(
  items: TreePickerItemWithValue<TKey>[],
  query: string,
): [
  /** the tree structure to be rendered */
  items: TreePickerItemWithValue<TKey>[],
  expandedNodes: Set<TKey>,
] => {
  if (query === '') {
    return [items, new Set()];
  }

  const expandedNodes: Set<TKey> = new Set();

  const result = produce({ items, expandedNodes }, (draft) => {
    draft.items = draft.items.filter(
      makeFilterTreePickerItemsByQuery(query, draft.expandedNodes),
    );
  });

  return [result.items, result.expandedNodes];
};
